// Design A ("Corporate Band") Service Invoice -- Print Preview only, used
// when institution_profile.print_template is 'design_a'. Mirrors
// backend/src/pdfTemplates/designA/serviceAdapter.js's config mapping.
// ServiceVoucherBody.jsx (legacy, still used for live entry preview and the
// 'legacy' print_template) is untouched.
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { numberFormat, numberFormatTax, formatToCompanyTimezone } from "../../../../../lib/functions";
import { convertAmountToWordsDesignA } from "../../../../../lib/designAWords";
import { fitItemName } from "../../../../../lib/designAItemFit";
import { formatPercent } from "../../../../../lib/designANumberFormat";
import { getServiceRecordWithDetailsApi } from "../../../../../apiservice/service";
import { getInstitutionApi } from "../../../../../apiservice/setting";
import { getPrimaryBankAccountApi } from "../../../../../apiservice/accounts";
import { bankAccountToConfig } from "../../../../../lib/designABankAccount";
import { selectAuthInfo } from "../../../../Auth/authSelectors";
import VoucherBodyDesignA from "../../../../../components/common/VoucherBodyDesignA";

// Row budgets recalibrated for the taller "rich item" row (name line + an
// optional item_description sub-line, see da-table--rich-item in
// VoucherBodyDesignA.css) -- ~42px/row vs. the default 28px, so ~2/3 as many
// rows fit per page. Mirrors backend/src/pdfTemplates/designA/serviceAdapter.js.
const SERVICE_ROW_BUDGETS = { single: 10, first: 16, middle: 20, last: 9 };

// Same fixed-layout column widths as serviceAdapter.js -- see that file's
// comment for the reasoning. Keep both in sync.
const PAGE_CONTENT_WIDTH = 698;
const COL_WIDTH = { hash: 28, rate: 100, per: 50, qty: 70, disc: 64, tax: 54, amount: 104 };
const CELL_PADDING = 16;
const CHAR_WIDTH_PX = 5.3;

function ItemNameCell({ name, maxChars }) {
  const fit = fitItemName(name, { maxChars });
  return <span className="da-item-name" style={{ fontSize: `${fit.fontSizePercent}%` }}>{fit.text}</span>;
}

function ItemCell({ name, maxChars, description }) {
  return (
    <div className="da-item-cell">
      <ItemNameCell name={name} maxChars={maxChars} />
      {description && <div className="da-item-sub">{description}</div>}
    </div>
  );
}

export default function ServiceVoucherBodyDesignA({ service_id, showStamp = false, showSignature = false, showShipping = false }) {
  const [data, setData] = useState(null);
  const [institution, setInstitution] = useState(null);
  const [primaryBankAccount, setPrimaryBankAccount] = useState(null);

  const { institutionData = {} } = useSelector((state) => state.lookups);
  const authInfo = useSelector(selectAuthInfo);

  useEffect(() => {
    if (!service_id) return;
    getServiceRecordWithDetailsApi({ service_id, from: "voucher" }).then((res) => {
      if (res?.data?.length) setData(res.data[0]);
    });
  }, [service_id]);

  useEffect(() => {
    getInstitutionApi().then((res) => setInstitution(res?.data || null)).catch(() => {});
    getPrimaryBankAccountApi().then((res) => setPrimaryBankAccount(res?.data?.data || null)).catch(() => {});
  }, []);

  if (!data) return null;

  const currency = institutionData?.currency || authInfo?.currency;
  const country = authInfo?.country || "";
  const isIndia = country.toLowerCase() === "india";
  const isIndiaGst = authInfo?.gst_enabled === "yes";
  const taxLabel = isIndia ? "GST" : "VAT";
  const isGSTDisabled = isIndia && authInfo?.gst_enabled == "no";
  const isUAE = country.trim().toLowerCase() === "united arab emirates";

  const isIndividual = data.is_cal_type === "individual";
  const items = data.details || [];

  const grandTotal = isIndividual
    ? items.reduce((sum, d) => {
        const qty = d.item_qty || 0;
        const rate = Number(d.item_rate || 0);
        let total = qty * rate;
        total -= (Number(d.item_discount_per || 0) / 100) * total;
        total += (Number(d.item_tax_per || 0) / 100) * total;
        return sum + total;
      }, 0)
    : data.total_amount || 0;
  const totalTax = Number(data.total_tax || 0);

  const useBranch = !!authInfo?.branch_info;
  const logoUrl = useBranch ? institution?.branch_logo : institution?.logo;
  const companyName = (useBranch ? institution?.branch_name : institution?.name) || "Institution";

  // Item cell sub-line: matches the legacy design (ServiceVoucherBody.jsx),
  // which shows item_description below the item name -- not a column.
  const usedWidth =
    COL_WIDTH.hash + COL_WIDTH.rate + COL_WIDTH.per + COL_WIDTH.qty +
    (isIndividual ? COL_WIDTH.disc : 0) + (isIndividual && !isGSTDisabled ? COL_WIDTH.tax : 0) + COL_WIDTH.amount;
  const itemMaxChars = Math.max(20, Math.floor((PAGE_CONTENT_WIDTH - usedWidth - CELL_PADDING) / CHAR_WIDTH_PX));

  const columns = [
    { label: "#", width: `${COL_WIDTH.hash}px` },
    { label: "Item Name", cellClassName: "da-item-td" },
    { label: "Rate", align: "right", width: `${COL_WIDTH.rate}px` },
    { label: "Per", align: "right", width: `${COL_WIDTH.per}px` },
    { label: "Quantity", align: "right", width: `${COL_WIDTH.qty}px` },
  ];
  if (isIndividual) {
    columns.push({ label: "Disc %", align: "right", width: `${COL_WIDTH.disc}px` });
    if (!isGSTDisabled) columns.push({ label: `${taxLabel} %`, align: "right", width: `${COL_WIDTH.tax}px` });
  }
  columns.push({ label: isIndividual ? "Total" : "Amount", align: "right", width: `${COL_WIDTH.amount}px` });

  const rows = items.map((d, i) => {
    const qtyDisplay = `${formatPercent(d.item_qty)} ${d.unit_symbol || ""}` + (d.conversion > 1 ? `, ${formatPercent(d.retail_qty)} ${d.base_unit_name}` : "");
    const cells = [
      String(i + 1),
      <ItemCell key="n" name={d.item_name} maxChars={itemMaxChars} description={d.item_description} />,
      <span className="da-num-cell" key="r">{numberFormat(d.item_rate)}</span>,
      <span className="da-num-cell" key="p">{isIndividual ? d.unit_name : d.per_unit_symbol}</span>,
      <span className="da-num-cell" key="q">{isIndividual ? formatPercent(d.item_qty) : qtyDisplay}</span>,
    ];

    let rawAmount;
    if (isIndividual) {
      cells.push(<span className="da-num-cell" key="d">{formatPercent(d.item_discount_per)}</span>);
      if (!isGSTDisabled) cells.push(<span className="da-num-cell" key="t">{formatPercent(d.item_tax_per)}</span>);
      let total = Number(d.item_qty || 0) * Number(d.item_rate || 0);
      total -= (Number(d.item_discount_per || 0) / 100) * total;
      total += (Number(d.item_tax_per || 0) / 100) * total;
      rawAmount = total;
    } else {
      rawAmount = Number(d.item_total || 0);
    }
    cells.push(<span className="da-amount-cell" key="a">{numberFormat(rawAmount)}</span>);

    return { cells, isBonus: false, rawAmount };
  });

  const totalsRows = [];
  if (!isIndividual) {
    totalsRows.push({ label: "Sub Total", value: numberFormat(data.sub_total || 0) });
    totalsRows.push({ label: "Discount", value: `- ${numberFormat(data.total_discount || 0)}`, credit: true });
  }

  let taxSummary = null;
  if (isIndia) {
    if (data.place_of_supply_type === "intra") {
      totalsRows.push({ label: "CGST", value: numberFormatTax(data.cgst_amount) });
      totalsRows.push({ label: "SGST", value: numberFormatTax(data.sgst_amount) });
      taxSummary = { mode: "cgst_sgst", rows: [{ rate: "", taxable: numberFormat(data.sub_total || 0), cgst: numberFormatTax(data.cgst_amount), sgst: numberFormatTax(data.sgst_amount) }] };
    } else if (data.place_of_supply_type === "inter") {
      totalsRows.push({ label: "IGST", value: numberFormatTax(data.igst_amount) });
      taxSummary = { mode: "igst", rows: [{ rate: "", taxable: numberFormat(data.sub_total || 0), igst: numberFormatTax(data.igst_amount) }] };
    }
  } else {
    totalsRows.push({ label: taxLabel, value: numberFormat(totalTax) });
  }

  const quantityTotal = items.reduce((sum, d) => sum + (Number(d.item_qty) || 0), 0);

  const bankDetails = bankAccountToConfig(primaryBankAccount);

  const config = {
    docTitle: "SERVICE INVOICE",
    docNumber: data.service_voucher_no,
    institution: { logoUrl, companyName, companyGstin: isIndia ? institutionData?.vat_license : null },
    party: {
      billToLabel: "Customer",
      billTo: {
        name: data.acc_name,
        lines: [
          data.acc_code ? `Customer Code: ${data.acc_code}` : null,
          data.address ? `Address: ${data.address}` : null,
          data.contact_no ? `Contact No: +${data.contact_no}` : null,
          data.licence_no ? `License No: ${data.licence_no}` : null,
        ].filter(Boolean),
      },
      shipTo:
        showShipping && data.shipping_address
          ? {
              lines: [
                data.shipping_contact_person ? `Contact Person: +${data.shipping_contact_person}` : null,
                `Address: ${data.shipping_address}`,
                data.shipping_po_box ? `Po Box: ${data.shipping_po_box}` : null,
                data.shipping_contact_no ? `Contact No: +${data.shipping_contact_no}` : null,
              ].filter(Boolean),
            }
          : null,
      fromLabel: "From",
      from: {
        name: companyName,
        lines: [
          institutionData?.street_address || null,
          institutionData?.phone_no ? `+${institutionData.phone_no}` : null,
        ].filter(Boolean),
      },
      metaLabel: "Voucher Details",
      meta: [
        { label: "Service Date", value: (formatToCompanyTimezone(data.created_date) || "").split(" ")[0] },
        { label: "Service By", value: (data.full_name || "").split(" ")[0] },
        { label: "Qty Total", value: String(quantityTotal) },
        ...(institutionData?.vat_license ? [{ label: isUAE ? "TRN No" : "Tax Reg No", value: institutionData.vat_license }] : []),
      ],
    },
    columns,
    rows,
    amountFn: (slice) => numberFormat(slice.reduce((sum, r) => sum + (r.rawAmount || 0), 0)),
    totalItems: items.length,
    totalQty: quantityTotal,
    totalsRows,
    grandTotalLabel: `Grand Total (${currency || ""})`,
    grandTotalValue: numberFormat(grandTotal),
    paidValue: numberFormat(data.paid_amount || 0),
    dueValue: numberFormat(data.due_amount || 0),
    amountInWordsText: convertAmountToWordsDesignA(grandTotal, { country, currency }),
    bankDetails,
    taxSummary,
    terms: institutionData?.invoice_default_terms || data.narration || "Thanks for your business.",
    signatory: {
      companyName,
      stampUrl: showStamp ? institutionData?.stamp : null,
      signatureUrl: showSignature ? institutionData?.signature : null,
    },
    bonusNote: null,
    rowBudgets: SERVICE_ROW_BUDGETS,
    tableClassName: "da-table--rich-item",
  };

  return <VoucherBodyDesignA config={config} />;
}
